import React, { useMemo, useState } from "react";
import { ChevronDown, ChevronUp, ArrowRight, ExternalLink } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { DIVISIONS } from "@/data/divisions";
import { DIV } from "@/constants/testIds";
import { useSubsidiaries } from "@/hooks/useSubsidiaries";

export default function Divisions() {
  const { hash } = useLocation();
  const [openId, setOpenId] = useState(hash ? hash.replace("#", "") : DIVISIONS[0].id);
  const { subs, loading } = useSubsidiaries();

  const byDivision = useMemo(() => {
    const map = {};
    for (const s of subs) {
      (map[s.division_id] = map[s.division_id] || []).push(s);
    }
    return map;
  }, [subs]);

  return (
    <div className="text-white">
      <section className="border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-24 pb-16">
          <div className="eyebrow mb-6">Operating Divisions</div>
          <h1 className="font-display font-black text-5xl md:text-7xl leading-[0.95] tracking-tighter max-w-4xl">
            Eight legal entities.<br />
            <span className="text-[#FF5722]">One coordinated deck.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-lg text-white/70 leading-relaxed">
            Each division below is a distinct operating company under Billtech Holdings. Expand a
            division to explore the specialist subsidiaries and services it commands.
          </p>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
        <div className="border border-white/10 bg-[#0C1322]">
          {DIVISIONS.map((d) => {
            const open = openId === d.id;
            const divSubs = byDivision[d.id] || [];
            return (
              <div key={d.id} id={d.id} data-testid={DIV.card(d.id)} className="border-b border-white/10 last:border-b-0">
                <button
                  className="w-full grid grid-cols-1 md:grid-cols-12 gap-4 p-6 lg:p-8 text-left group hover:bg-white/[0.02] transition-colors"
                  onClick={() => setOpenId(open ? null : d.id)}
                  data-testid={DIV.viewBtn(d.id)}
                >
                  <div className="md:col-span-1 font-mono text-[11px] uppercase tracking-[0.25em] text-[#FF5722]">
                    {d.code}
                  </div>
                  <div className="md:col-span-8">
                    <div className="font-display text-2xl md:text-3xl font-bold text-white leading-tight tracking-tight">
                      {d.name}
                    </div>
                    <div className="mt-2 text-sm text-white/70 max-w-3xl">{d.tagline}</div>
                  </div>
                  <div className="md:col-span-2 flex md:justify-end items-center">
                    <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/60">
                      {loading ? "…" : `${divSubs.length} entities`}
                    </span>
                  </div>
                  <div className="md:col-span-1 flex md:justify-end items-center">
                    {open ? <ChevronUp size={20} className="text-[#FF5722]" /> : <ChevronDown size={20} className="text-white/60 group-hover:text-[#FF5722]" />}
                  </div>
                </button>

                {open && (
                  <div className="border-t border-white/10 bg-[#060A14]">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 p-6 lg:p-10">
                      <div className="lg:col-span-4">
                        <div
                          className="aspect-[4/3] bg-cover bg-center border border-white/10"
                          style={{ backgroundImage: `url(${d.image})` }}
                        />
                        <p className="mt-5 text-white/75 leading-relaxed text-sm">{d.overview}</p>
                        <div className="mt-5 flex flex-wrap gap-3">
                          <Link
                            to={`/subsidiaries?division=${d.id}`}
                            className="btn-secondary text-xs"
                            data-testid={`view-subs-${d.id}`}
                          >
                            View Subsidiaries <ArrowRight size={14} />
                          </Link>
                          <Link
                            to={`/contact?division=${d.id}`}
                            className="btn-primary text-xs"
                            data-testid={`request-${d.id}`}
                          >
                            Request Scope <ArrowRight size={14} />
                          </Link>
                        </div>
                      </div>

                      <div className="lg:col-span-8">
                        <div className="eyebrow mb-4">Subsidiary Companies</div>
                        {loading ? (
                          <div className="text-white/50 text-sm">Loading subsidiaries…</div>
                        ) : divSubs.length === 0 ? (
                          <div className="text-white/50 text-sm">No subsidiaries assigned yet.</div>
                        ) : (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {divSubs.map((s) => (
                              <SubMiniCard key={s.id} s={s} />
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}

function SubMiniCard({ s }) {
  const hasLink = !!s.website_url;
  return (
    <div className="border border-white/10 bg-[#0C1322] p-4 hover:border-[#FF5722] transition-colors">
      <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#FF5722]">
        A Billtech Holdings Company
      </div>
      <div className="mt-1 font-display text-[15px] font-bold text-white leading-snug">
        {hasLink ? (
          <a
            href={s.website_url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`sub-link-${s.id}`}
            className="inline-flex items-center gap-1.5 hover:text-[#FF5722] transition-colors"
          >
            {s.name} <ExternalLink size={12} className="opacity-70" />
          </a>
        ) : (
          s.name
        )}
      </div>
      <div className="mt-2 text-xs text-[#94A3B8]">
        <span className="text-white/90 font-medium">{s.service}</span> · {s.blurb}
      </div>
    </div>
  );
}
